import { AccountInfo } from '../models/account';
import { AccountBindResponse, EmailIdentityRequest, IdentityLoginToken, JwtToken, VerifyIdentityRequest } from '../models/auth';
import { LinkInfo } from "../models/link";
import { InvoiceInfo, PayHistoryInfo, PayType } from "../models/payment";
import { PlatformCard } from "../models/platform";
import { ResultResponse } from "../models/result";
import { ServerInfo } from "../models/servers";
import { CheckSubscriptionResult, Subscription } from "../models/subscriptions";
import { TelegramProxyLink } from "../models/tgProxy";
import { fetcher, setHeaderProvider } from './fetcher';
import type { IRestClient } from './RestClientContext';

export class SubscriptionNotFoundError extends Error { }

export class RestClient implements IRestClient {
    readonly baseUrl: string;
    readonly initData: string;

    constructor(baseUrl: string, initData: string) {
        this.baseUrl = baseUrl;
        this.initData = initData;

        setHeaderProvider(() => ({
            ...(this.initData ? { 'X-tg-data': this.initData } : {}),
            ...(localStorage.getItem('ogo.jwt') ? { 'Authorization': `Bearer ${localStorage.getItem('ogo.jwt')}` } : {}),
            'Content-Type': 'application/json',
        }));
    }

    checkSubscription = async (): Promise<CheckSubscriptionResult> => {
        return await fetcher<CheckSubscriptionResult>({
            input: this.buildUrl('/sub/check'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка проверки подписки: ' + msg
        });
    }

    createSubscription = async (ref?: string): Promise<ResultResponse<Subscription>> => {
        return await fetcher<ResultResponse<Subscription>>({
            input: this.buildUrl('/sub' + (ref ? '?ref=' + ref : '')), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка создания подписки: ' + msg
        });
    }

	getPlatforms = async(): Promise<ResultResponse<PlatformCard[]>> =>  {
        return await fetcher<ResultResponse<PlatformCard[]>>({
            input: this.buildUrl('/pub/install-instruction'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения инструкций: ' + msg
        });
	}

    getPayTypes = async (): Promise<PayType[]> => {
        return await fetcher<PayType[]>({
            input: this.buildUrl('/pub/pay-types'), init: {
                method: 'GET'
            }
        });
    }

	termOfUse = async(): Promise<ResultResponse<string>> =>  {
        return await fetcher<ResultResponse<string>>({
            input: this.buildUrl('/pub/termofuse'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения инструкций: ' + msg
        });
	}

    getServersInternal = async (): Promise<ServerInfo[]> => {
        return await fetcher<ServerInfo[]>({
            input: this.buildUrl('/sub/latency'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения серверов: ' + msg
        });
    }

    getServers = async (): Promise<ServerInfo[]> => {
        return await fetcher<ServerInfo[]>({
            input: this.buildUrl('/pub/servers'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения серверов: ' + msg
        });
    }

    getLink = async (): Promise<LinkInfo> => {
        return await fetcher<LinkInfo>({
            input: this.buildUrl('/sub/link'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения ссылки: ' + msg
        });
    }

    getTelegramProxyLinks = async (): Promise<TelegramProxyLink[]> => {
        return await fetcher<TelegramProxyLink[]>({
            input: this.buildUrl('/pub/telegram-proxy-links'),
            init: {
                method: 'GET'
            },
            mapToastMsg: (msg) => 'Ошибка получения Telegram прокси: ' + msg
        });
    }

    getPayHistory = async (page: number, size: number, type: string[]): Promise<PayHistoryInfo[]> => {
        return await fetcher<PayHistoryInfo[]>({
            input: this.buildUrl(`/payment/youkassa/payhs?page=${page}&size=${size}&status=${type.join(',')}`), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения истории платежей: ' + msg
        });
    }

    createInvoice = async (type: string): Promise<InvoiceInfo> => {
        return await fetcher<InvoiceInfo>({
            input: this.buildUrl('/youmoney/invoice?type=' + type), init: {
                method: 'POST'
            }, mapToastMsg: (msg) => 'Ошибка создания платежа: ' + msg
        });
    }

    fetchTgPaymentLink = async (type: string): Promise<LinkInfo> => {
        return await fetcher<LinkInfo>({
            input: this.buildUrl('/payment/youkassa/invoice-link?type=' + type), init: {
                method: 'POST'
            }, mapToastMsg: (msg) => 'Ошибка получения ссылки: ' + msg
        });
    }

    getReferralHash = async (): Promise<ResultResponse<string>> => {
        return await fetcher<ResultResponse<string>>({
            input: this.buildUrl('/referal/link'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения реферальной ссылки: ' + msg
        });
    }

    getReferralsCount = async (): Promise<ResultResponse<number>> => {
        return await fetcher<ResultResponse<number>>({
            input: this.buildUrl('/referal/count'), init: {
                method: 'GET'
            }, mapToastMsg: (msg) => 'Ошибка получения количества рефералов: ' + msg
        });
    }

    happCrypto = async (url: string): Promise<ResultResponse<string>> => {
        return await fetcher<ResultResponse<string>>({
            input: this.buildUrl('/sub/happ-crypto'), init: {
                method: 'POST',
                body: JSON.stringify({ url }),
            }
        });
    }

    bindEmail = async (request: EmailIdentityRequest): Promise<ResultResponse<AccountBindResponse>> => {
        return await fetcher<ResultResponse<AccountBindResponse>>({
            input: this.buildUrl('/account/bind'),
            init: { method: 'POST', body: JSON.stringify(request) },
            mapToastMsg: (msg) => 'Ошибка привязки email: ' + msg,
        });
    }

    verifyEmailBinding = async (request: VerifyIdentityRequest): Promise<ResultResponse<void>> => {
        return await fetcher<ResultResponse<void>>({
            input: this.buildUrl('/account/bind/verify'),
            init: { method: 'POST', body: JSON.stringify(request) },
            errorMessage: 'Код неверен или срок его действия истек. Попробуйте запросить новый код.',
            mapToastMsg: () => 'Не удалось подтвердить email. Проверьте код и попробуйте еще раз.',
        });
    }

    loginByEmail = async (request: EmailIdentityRequest): Promise<IdentityLoginToken> => {
        return await fetcher<IdentityLoginToken>({
            input: this.buildUrl('/auth/login'),
            init: { method: 'POST', body: JSON.stringify(request) },
            mapToastMsg: (msg) => 'Ошибка входа: ' + msg,
        });
    }

    verifyEmailLogin = async (request: VerifyIdentityRequest): Promise<JwtToken> => {
        return await fetcher<JwtToken>({
            input: this.buildUrl('/auth/login/continue'),
            init: { method: 'POST', body: JSON.stringify(request) },
            errorMessage: 'Код неверен или срок его действия истек. Попробуйте запросить новый код.',
            mapToastMsg: () => 'Не удалось войти. Проверьте код и попробуйте еще раз.',
        });
    }

    getAccountInfo = async (): Promise<ResultResponse<AccountInfo>> => {
        return await fetcher<ResultResponse<AccountInfo>>({
            input: this.buildUrl('/account/me'),
            init: { method: 'GET' },
            mapToastMsg: (msg) => 'Ошибка получения данных аккаунта: ' + msg,
        });
    }

    private readonly buildUrl = (path: string): string => {
        return `${this.baseUrl}${path}`;
    };

}
